import { useScenario } from '@/features/decisions/hooks/useScenario';
import type { ExperimentLabel } from '@/features/decisions/scenarios.api.types';
import { parseExperimentIds } from '@/features/decisions/utils/fcfFormat';

export const MAX_COMPARISON_CANDIDATES = 5;

interface Args {
  scenarioRunId: string;
  runId: string;
  // The persona's own resolved value and whether the caller pinned it explicitly (via URL or a
  // prior commit) — when it wasn't, this run's own candidates replace the hardcoded per-page
  // default instead of layering on top of it.
  experimentIdRaw: string;
  hasExplicitExperimentId: boolean;
}

// This run's own comparison candidates — excludes the position-1 reference/incumbent, which
// ref_experiment_id covers separately — plus the effective experiment_id/experimentIds a
// consumer should actually use. Kept apart from useActivePersonaContext: that hook is
// unit-tested directly against a bare router with no QueryClientProvider/TenantProvider, and
// its returned surface is contract-pinned across all three pages that read it.
export function useComparisonCandidates({ scenarioRunId, runId, experimentIdRaw, hasExplicitExperimentId }: Args) {
  const { data: scenario } = useScenario(scenarioRunId);
  const execution = scenario?.executions.find((e) => e.run_id === runId);

  const candidates: ExperimentLabel[] = (execution?.experiments ?? [])
    .filter((e) => e.position !== 1)
    .sort((a, b) => a.position - b.position);

  const topCandidateIds = candidates.slice(0, MAX_COMPARISON_CANDIDATES).map((e) => e.experiment_id);

  const sourceIds =
    !hasExplicitExperimentId && topCandidateIds.length > 0 ? topCandidateIds : parseExperimentIds(experimentIdRaw);

  // Capped regardless of source: an explicit experiment_id can still carry more than
  // MAX_COMPARISON_CANDIDATES — e.g. the Runs page's "View summary" link, built from every
  // comparison the run had rather than a capped subset. Without this, such a link would show
  // (and query) every one of them instead of a swappable 5.
  const effectiveExperimentIds = sourceIds.slice(0, MAX_COMPARISON_CANDIDATES);
  const effectiveExperimentIdRaw = effectiveExperimentIds.join(', ');

  return {
    candidates,
    experimentIdRaw: effectiveExperimentIdRaw,
    experimentIds: effectiveExperimentIds,
  };
}
